import { useCallback, useEffect, useRef, useState } from 'react';
import Input from '../components/Input';
import JsonBlock from '../components/JsonBlock';
import {
  getXoxodaySettings,
  switchXoxodayEnvironment,
  testXoxodayConnection,
  updateXoxodayCredentials,
} from '../services/giftCardService';
import {
  XOXODAY_DEFAULT_HOSTS,
  formatDateTime,
  formatNumber,
  formatRelativeTime,
  getCredentialSourceLabel,
  getEnvLabel,
  getExpiryDisplay,
} from '../constants/giftCard';

const TOAST_DURATION_MS = 4000;
const ENVIRONMENTS = ['test', 'live'];

const SECRET_FIELDS = [
  { key: 'accessToken', label: 'Access token' },
  { key: 'refreshToken', label: 'Refresh token' },
  { key: 'clientId', label: 'Client ID' },
  { key: 'clientSecret', label: 'Client secret' },
];

const EMPTY_SECRETS = { accessToken: '', refreshToken: '', clientId: '', clientSecret: '' };

function otherEnvironment(environment) {
  return environment === 'live' ? 'test' : 'live';
}

function getSecretPlaceholder(credential) {
  if (!credential?.set) return 'Not set — enter a value to add it';
  return `Current: ${credential.masked || '••••'} — leave blank to keep`;
}

function getHost(env, environment) {
  return env?.host || XOXODAY_DEFAULT_HOSTS[environment] || '—';
}

// Backend may send `valid` or `ok`; treat a missing flag as unknown (neutral).
function isCheckValid(check) {
  if (typeof check?.valid === 'boolean') return check.valid;
  if (typeof check?.ok === 'boolean') return check.ok;
  return null;
}

function ConnectionResult({ result }) {
  if (!result) return null;

  const tokenMessage = result.accessToken?.message;
  const tokenValid = isCheckValid(result.accessToken);
  const tokenLine = tokenMessage ? (
    <span className={`gc-connection-token ${tokenValid === false ? 'gc-cred-expired' : ''}`.trim()}>{tokenMessage}</span>
  ) : null;

  if (result.ok) {
    return (
      <div className="gc-connection gc-connection-ok" role="status">
        <span>Connected — {formatNumber(result.productCount)} products ({formatNumber(result.ms)} ms)</span>
        {tokenLine}
      </div>
    );
  }

  return (
    <div className="gc-connection gc-connection-failed" role="status">
      <span>Failed: {result.error || 'Unknown error'}</span>
      {tokenLine}
      <JsonBlock label="Details" value={result.details} />
    </div>
  );
}

function PastedTokenCheck({ check }) {
  if (!check?.message) return null;
  const valid = isCheckValid(check);
  return (
    <div className={`gc-connection ${valid ? 'gc-connection-ok' : 'gc-connection-failed'}`} role="status">
      {valid ? check.message : `Warning: ${check.message}`}
    </div>
  );
}

function CredentialItem({ label, credential, showExpiry = false }) {
  const expiry = showExpiry && credential?.set ? getExpiryDisplay(credential.expiresAt) : null;

  return (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <span className={`detail-value ${credential?.set ? 'detail-value-mono' : ''}`.trim()}>
        {credential?.set ? credential.masked || '••••' : 'Not set'}
      </span>
      {expiry ? (
        <span
          className={`gc-cred-meta ${expiry.expired ? 'gc-cred-expired' : ''}`.trim()}
          title={credential.expiresAt ? formatDateTime(credential.expiresAt) : undefined}
        >
          {expiry.text}
        </span>
      ) : null}
      <span className="gc-cred-meta">{getCredentialSourceLabel(credential?.source)}</span>
    </div>
  );
}

function EnvironmentCard({ environment, env, connectionResult, testing, onTest, onUpdate }) {
  const label = getEnvLabel(environment);

  return (
    <div className={`user-detail-card gc-provider-card ${env?.isActive ? 'is-active' : ''}`.trim()}>
      <div className="gc-provider-card-head">
        <h4 className="gc-provider-card-title">
          {label} — {getHost(env, environment)}
        </h4>
        <div className="gc-status-cell">
          {env?.isActive ? <span className={`gc-env-badge gc-env-${environment}`}>Active</span> : null}
          {env?.configured ? (
            <span className="status-badge status-success">Configured</span>
          ) : (
            <span className="status-badge status-pending">Not configured</span>
          )}
        </div>
      </div>

      <div className="detail-grid gc-provider-grid">
        <CredentialItem label="Access token" credential={env?.accessToken} showExpiry />
        <CredentialItem label="Refresh token" credential={env?.refreshToken} showExpiry />
        <CredentialItem label="Client ID" credential={env?.clientId} />
        <CredentialItem label="Client secret" credential={env?.clientSecret} />
        <div className="detail-item">
          <span className="detail-label">Last updated</span>
          <span className="detail-value">{env?.updatedAt ? formatDateTime(env.updatedAt) : 'Never updated in admin'}</span>
          {env?.updatedByAdminId ? <span className="gc-cred-meta">by {env.updatedByAdminId}</span> : null}
        </div>
      </div>

      <div className="trip-detail-actions gc-provider-actions">
        <button type="button" className="trip-action-btn" onClick={onTest} disabled={testing}>
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        <button type="button" className="trip-action-btn" onClick={onUpdate}>
          Update credentials
        </button>
      </div>

      <ConnectionResult result={connectionResult} />
    </div>
  );
}

// Rendered only while open, so secret inputs are discarded whenever it closes.
function CredentialsModal({ environment, env, onClose, onSaved }) {
  const [secrets, setSecrets] = useState(EMPTY_SECRETS);
  const [testAfterSave, setTestAfterSave] = useState(true);
  const [confirmingLive, setConfirmingLive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Result of the last save, shown under the form; the inputs are cleared at the same time.
  const [saveResult, setSaveResult] = useState(null);

  const label = getEnvLabel(environment);
  const isLive = environment === 'live';
  const hasSecret = SECRET_FIELDS.some(({ key }) => secrets[key].trim());

  function buildPayload() {
    const payload = {};
    SECRET_FIELDS.forEach(({ key }) => {
      const value = secrets[key].trim();
      if (value) payload[key] = value;
    });
    payload.test = testAfterSave;
    return payload;
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!hasSecret || saving) return;

    if (isLive && env?.isActive && !confirmingLive) {
      setConfirmingLive(true);
      return;
    }

    setSaving(true);
    setError('');
    setSaveResult(null);

    try {
      const response = await updateXoxodayCredentials(environment, buildPayload());
      const data = response?.data || {};
      const hasResult = Boolean(data.pastedTokenCheck?.message || data.connection);
      onSaved(response?.message || 'Credentials saved.', data, { keepOpen: hasResult });
      if (hasResult) {
        setSecrets(EMPTY_SECRETS);
        setConfirmingLive(false);
        setSaveResult({ pastedTokenCheck: data.pastedTokenCheck, connection: data.connection });
        setSaving(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to save credentials.');
      setConfirmingLive(false);
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay gift-card-review-overlay" onClick={saving ? undefined : onClose}>
      <form
        className="modal-content gift-card-review-modal gc-provider-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        autoComplete="off"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-credentials-title"
      >
        <h3 id="gc-credentials-title" className="modal-title">Update {label} credentials</h3>
        <p className="modal-message">
          Fill in only what you want to replace. Saving does not switch the active account.
        </p>

        {isLive ? (
          <div className="gc-alert gc-alert-danger gc-provider-modal-alert">
            <span className="gc-alert-title">These are real-money credentials.</span>
          </div>
        ) : null}

        <div className="password-form-fields gc-cred-fields">
          {SECRET_FIELDS.map(({ key, label: fieldLabel }) => (
            <Input
              key={key}
              id={`gc-${environment}-${key}`}
              label={fieldLabel}
              type="password"
              // "off" is ignored for password inputs; this stops the browser filling in the admin's saved login.
              autoComplete="new-password"
              value={secrets[key]}
              disabled={saving}
              placeholder={getSecretPlaceholder(env?.[key])}
              onChange={(e) => setSecrets((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          ))}

          <label className="gc-checkbox">
            <input
              type="checkbox"
              checked={testAfterSave}
              disabled={saving}
              onChange={(e) => setTestAfterSave(e.target.checked)}
            />
            Test connection after saving
          </label>
        </div>

        {confirmingLive ? (
          <div className="gc-alert gc-alert-danger gc-provider-modal-alert">
            <span className="gc-alert-title">LIVE is active — new credentials take effect immediately for real orders.</span>
            <span className="gift-card-failure-text">Press “Save anyway” to continue.</span>
          </div>
        ) : null}

        {error ? <div className="add-restaurant-error gc-provider-modal-error">{error}</div> : null}

        {saveResult ? (
          <div className="gc-save-result">
            <PastedTokenCheck check={saveResult.pastedTokenCheck} />
            <ConnectionResult result={saveResult.connection} />
          </div>
        ) : null}

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose} disabled={saving}>
            {saveResult ? 'Close' : 'Cancel'}
          </button>
          <button
            type="submit"
            className={`modal-btn modal-btn-confirm ${confirmingLive ? 'gift-card-review-confirm-reject' : ''}`.trim()}
            disabled={!hasSecret || saving}
          >
            {saving ? 'Saving…' : confirmingLive ? 'Save anyway' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function getSwitchFailureCopy(failure, label) {
  if (failure?.reason === 'NO_PRODUCTS') {
    return { title: `Not switched to ${label}.`, text: 'Connected, but the account has no products.' };
  }
  return {
    title: `Couldn't connect to ${label} — not switched.`,
    text: failure?.connection?.error || failure?.message || 'Connection check failed.',
  };
}

// stage: 'confirm' → (409) 'failed' → 'force-confirm'
function SwitchModal({ target, onClose, onSwitched }) {
  const [stage, setStage] = useState('confirm');
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState(null);

  const label = getEnvLabel(target);
  const toLive = target === 'live';
  const canConfirm = !toLive || typed.trim() === 'LIVE';

  async function submit(force) {
    if (submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const response = await switchXoxodayEnvironment(target, { force });
      onSwitched(response?.message || `Switched to ${label}.`, response?.data || {});
    } catch (err) {
      if (err.status === 409) {
        setFailure({ ...(err.data || {}), message: err.message });
        setStage('failed');
      } else {
        setError(err.message || 'Failed to switch environment.');
      }
      setSubmitting(false);
    }
  }

  const failureCopy = getSwitchFailureCopy(failure, label);

  return (
    <div className="modal-overlay gift-card-review-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="modal-content gift-card-review-modal gc-provider-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-switch-title"
      >
        <h3 id="gc-switch-title" className="modal-title">
          {stage === 'force-confirm' ? `Force switch to ${label}?` : `Switch to ${label}?`}
        </h3>

        {stage === 'confirm' ? (
          <>
            <p className="modal-message">
              {toLive
                ? "New gift card orders will be placed on the real Xoxoday account and charged real money. The gift card list will be replaced with the live catalog. We'll test the connection first and won't switch if it fails."
                : 'New orders will go to the test account — users will NOT receive real gift cards.'}
            </p>
            {toLive ? (
              <label className="add-restaurant-field gc-switch-type">
                <span>Type LIVE to confirm</span>
                <input
                  type="text"
                  value={typed}
                  autoComplete="off"
                  disabled={submitting}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </label>
            ) : null}
          </>
        ) : null}

        {stage === 'failed' ? (
          <div className="gc-alert gc-alert-danger gc-provider-modal-alert">
            <span className="gc-alert-title">{failureCopy.title}</span>
            <span className="gift-card-failure-text">{failureCopy.text}</span>
            <JsonBlock label="Connection details" value={failure?.connection?.details} />
          </div>
        ) : null}

        {stage === 'force-confirm' ? (
          <p className="modal-message">
            The gift card list will be EMPTY until {label} starts working. Only do this in an emergency.
          </p>
        ) : null}

        {submitting ? <p className="gc-switch-loading">Testing connection and loading catalog…</p> : null}
        {error ? <div className="add-restaurant-error gc-provider-modal-error">{error}</div> : null}

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose} disabled={submitting}>
            {stage === 'failed' ? 'Close' : 'Cancel'}
          </button>
          {stage === 'confirm' ? (
            <button
              type="button"
              className={`modal-btn modal-btn-confirm ${toLive ? 'gift-card-review-confirm-reject' : ''}`.trim()}
              onClick={() => submit(false)}
              disabled={!canConfirm || submitting}
            >
              {submitting ? 'Switching…' : `Switch to ${label}`}
            </button>
          ) : null}
          {stage === 'failed' ? (
            <button
              type="button"
              className="modal-btn modal-btn-confirm gift-card-review-confirm-reject"
              onClick={() => setStage('force-confirm')}
            >
              Switch anyway
            </button>
          ) : null}
          {stage === 'force-confirm' ? (
            <button
              type="button"
              className="modal-btn modal-btn-confirm gift-card-review-confirm-reject"
              onClick={() => submit(true)}
              disabled={submitting}
            >
              {submitting ? 'Switching…' : `Force switch to ${label}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="user-detail-content" aria-busy="true" aria-label="Loading provider settings">
      <div className="user-detail-card gc-skeleton gc-skeleton-banner" />
      <div className="gc-provider-cards">
        <div className="user-detail-card gc-skeleton gc-skeleton-card" />
        <div className="user-detail-card gc-skeleton gc-skeleton-card" />
      </div>
    </div>
  );
}

export default function GiftCardProviderSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectionResults, setConnectionResults] = useState({});
  const [testing, setTesting] = useState({});
  const [editingEnv, setEditingEnv] = useState(null);
  const [switchTarget, setSwitchTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const loadSettings = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const response = await getXoxodaySettings();
      setSettings(response?.data || null);
      setError('');
    } catch (err) {
      // A failed background refresh keeps the data already on screen.
      if (!silent) setError(err.message || 'Failed to load provider settings.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(text, kind = 'success') {
    clearTimeout(toastTimer.current);
    setToast({ text, kind });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }

  function setConnectionResult(environment, result) {
    setConnectionResults((prev) => ({ ...prev, [environment]: result }));
  }

  async function handleTest(environment) {
    setTesting((prev) => ({ ...prev, [environment]: true }));
    setConnectionResult(environment, null);

    try {
      const response = await testXoxodayConnection(environment);
      const result = response?.data || null;
      setConnectionResult(environment, result);
      if (response?.message) showToast(response.message, result?.ok ? 'success' : 'error');
    } catch (err) {
      setConnectionResult(environment, { ok: false, environment, error: err.message || 'Request failed.' });
    } finally {
      setTesting((prev) => ({ ...prev, [environment]: false }));
    }
  }

  function handleCredentialsSaved(message, data, { keepOpen = false } = {}) {
    const environment = editingEnv;
    if (!keepOpen) setEditingEnv(null);
    showToast(message, 'success');
    if (data.connection) setConnectionResult(environment, data.connection);
    loadSettings({ silent: true });
  }

  function handleSwitched(message, data) {
    setSwitchTarget(null);
    showToast(message, data.switched === false ? 'warning' : 'success');
    if (data.connection && data.activeEnvironment) setConnectionResult(data.activeEnvironment, data.connection);
    loadSettings({ silent: true });
  }

  if (loading) {
    return (
      <div className="gift-card-detail-page main-bg-top">
        <SettingsSkeleton />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="gift-card-detail-page main-bg-top">
        <div className="user-detail-error">
          <p>{error || 'Provider settings are unavailable.'}</p>
          <button className="btn btn-primary" type="button" onClick={() => loadSettings()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const active = settings.activeEnvironment === 'live' ? 'live' : 'test';
  const target = otherEnvironment(active);
  const environments = settings.environments || {};
  const targetConfigured = environments[target]?.configured === true;
  const isLive = active === 'live';
  const changedAt = settings.activeEnvironmentChangedAt;

  return (
    <div className="gift-card-detail-page main-bg-top">
      <div className="user-detail-content">
        <div className={`user-detail-card gc-provider-banner ${isLive ? 'is-live' : ''}`.trim()}>
          <div className="gc-provider-banner-text">
            <span className="gc-provider-banner-eyebrow">Xoxoday account</span>
            <h3 className="gc-provider-banner-title">Gift cards are using {getEnvLabel(active)}</h3>
            <span className="gift-card-detail-po">{getHost(environments[active], active)}</span>
            <span className="gc-provider-banner-meta" title={changedAt ? formatDateTime(changedAt) : undefined}>
              {changedAt
                ? `Changed ${formatRelativeTime(changedAt)} by ${settings.activeEnvironmentChangedByAdminId || 'unknown admin'}`
                : 'Default from server configuration'}
            </span>
          </div>
          <span title={targetConfigured ? undefined : `Add ${getEnvLabel(target)} credentials first`}>
            <button
              type="button"
              className={`btn ${target === 'live' ? 'btn-danger' : 'btn-secondary'} gc-provider-switch-btn`}
              disabled={!targetConfigured}
              onClick={() => setSwitchTarget(target)}
            >
              Switch to {getEnvLabel(target)}
            </button>
          </span>
        </div>

        <div className="gc-provider-cards">
          {ENVIRONMENTS.map((environment) => (
            <EnvironmentCard
              key={environment}
              environment={environment}
              env={environments[environment]}
              connectionResult={connectionResults[environment]}
              testing={Boolean(testing[environment])}
              onTest={() => handleTest(environment)}
              onUpdate={() => setEditingEnv(environment)}
            />
          ))}
        </div>
      </div>

      {editingEnv ? (
        <CredentialsModal
          environment={editingEnv}
          env={environments[editingEnv]}
          onClose={() => setEditingEnv(null)}
          onSaved={handleCredentialsSaved}
        />
      ) : null}

      {switchTarget ? (
        <SwitchModal target={switchTarget} onClose={() => setSwitchTarget(null)} onSwitched={handleSwitched} />
      ) : null}

      {toast ? (
        <div className={`gc-toast gc-toast-${toast.kind}`} role="status">{toast.text}</div>
      ) : null}
    </div>
  );
}
